import { useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Download } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Common";
import { Missing } from "@/components/si/Bits";
import { Blueprint } from "@/components/si/Blueprint";
import { Searches } from "@/components/si/Searches";
import { Candidates } from "@/components/si/Candidates";
import { Pipeline } from "@/components/si/Pipeline";
import { Compare } from "@/components/si/Compare";
import { RadarBoard } from "@/components/si/RadarBoard";
import { Assistant } from "@/components/si/Assistant";
import { Insights } from "@/components/si/Insights";
import { SI, STEPS, pretty, siErr, siPost, useSI, useSiMe } from "@/lib/sourcing";
import { cn } from "@/lib/utils";

const TABS = [["blueprint", "Blueprint"], ["searches", "Boolean & X-Ray"], ["candidates", "Candidates"], ["pipeline", "Pipeline"], ["compare", "Compare"], ["radar", "Job Radar"], ["assistant", "AI Assistant"], ["insights", "Optimize & Market"]];
const VIEW = { blueprint: Blueprint, searches: Searches, candidates: Candidates, pipeline: Pipeline, compare: Compare, radar: RadarBoard, assistant: Assistant, insights: Insights };

const Stepper = ({ at }) => (
  <div className="flex gap-1 overflow-x-auto rounded-xl border border-[#27272A] bg-[#121215] p-1.5" data-testid="si-stepper">
    {STEPS.map((s, i) => <span key={s} className={cn("shrink-0 rounded-lg px-2 py-1 font-mono text-[10px] transition-colors", i < at ? "text-[#D4AF37]" : i === at ? "bg-[#8224e3] text-white" : "text-zinc-600")} data-testid={`si-step-${i}`}>{i + 1}. {s}</span>)}
  </div>
);

export default function SIProject() {
  const { id, tab = "blueprint" } = useParams();
  const me = useSiMe();
  const p = useSI(`${SI}/projects/${id}`);
  const [selected, setSel] = useState(() => { try { return JSON.parse(sessionStorage.getItem(`si_sel_${id}`) || "[]"); } catch { return []; } });
  const setSelected = (v) => { sessionStorage.setItem(`si_sel_${id}`, JSON.stringify(v)); setSel(v); };
  const dup = async () => { try { const r = await siPost(`/projects/${id}/duplicate`, {}); toast.success("Project duplicated"); window.location.assign(`${SI}/p/${r.id}`); } catch (e) { toast.error(siErr(e)); } };
  const V = VIEW[tab] || Blueprint;
  const d = p.data;
  return (
    <AdminShell area="si" title={d?.name || "Sourcing project"} subtitle={d ? [d.job?.title, d.job?.company, d.job?.location, d.recruiter_name && `Owner: ${d.recruiter_name}`].filter(Boolean).join(" · ") : ""}
      actions={d && <><span className="self-center rounded-full border border-[#27272A] px-3 py-1 text-[10px] uppercase tracking-wider text-zinc-400" data-testid="si-project-status">{pretty(d.status || "draft")}{d.spec_version ? ` · spec v${d.spec_version}` : ""}</span>
        <Button variant="outline" size="sm" onClick={dup} className="border-[#27272A]" data-testid="si-duplicate"><Copy className="h-3.5 w-3.5" />Duplicate</Button>
        {me.canExport && <Button asChild variant="outline" size="sm" className="border-[#27272A]"><Link to={`${SI}/p/${id}/candidates?export=1`} data-testid="si-export-link"><Download className="h-3.5 w-3.5" />Export</Link></Button>}</>}>
      <div className="space-y-5" data-testid="si-project">
        <Link to={SI} className="text-xs text-zinc-500 hover:text-[#D4AF37]" data-testid="si-back">← All sourcing projects</Link>
        {p.missing ? <Missing what="Needs GET /admin/si/projects/{id}." /> : !d ? (p.error ? <p className="text-sm text-red-400">{p.error}</p> : <Spinner className="h-6 w-6 text-[#D4AF37]" />) : <>
          <Stepper at={d.step ?? (d.approved ? 5 : d.blueprint ? 3 : 1)} />
          <nav className="flex gap-1 overflow-x-auto border-b border-[#27272A] pb-2" data-testid="si-tabs">{TABS.map(([k, l]) => (
            <NavLink key={k} to={`${SI}/p/${id}/${k}`} className={cn("shrink-0 rounded-full px-3.5 py-1.5 text-xs transition-colors", k === tab ? "bg-[#D4AF37] font-semibold text-black" : "text-zinc-400 hover:bg-[#18181B] hover:text-zinc-100")} data-testid={`si-tab-${k}`}>{l}{k === "compare" && selected.length ? ` (${selected.length})` : ""}</NavLink>
          ))}</nav>
          <V project={d} reload={p.reload} setProject={p.setData} selected={selected} setSelected={setSelected} />
        </>}
      </div>
    </AdminShell>
  );
}
